import { useState } from 'react';
import { settingsService } from '../services/settingsService';
import { buildApiUrl } from '../config/environment';

export interface AppSettings {
  restaurant_radius_km: number;
  supercharger_radius_km: number;
  tesla_supercharger: boolean;
  ionity: boolean;
  okq8: boolean;
  unit_system?: 'metric' | 'imperial';
  currency?: 'SEK' | 'USD' | 'EUR' | 'CHF' | 'DKK' | 'NOK';
}

export interface User {
  id: number;
  username: string;
  displayName?: string;
}

export interface UseSettingsReturn {
  // State
  settings: AppSettings;
  setSettings: React.Dispatch<React.SetStateAction<AppSettings>>;
  settingsError: string;
  setSettingsError: React.Dispatch<React.SetStateAction<string>>;
  priceRange: string;
  setPriceRange: React.Dispatch<React.SetStateAction<string>>;
  maxRestaurants: number;
  setMaxRestaurants: React.Dispatch<React.SetStateAction<number>>;
  subscriptionInfo: any;
  setSubscriptionInfo: React.Dispatch<React.SetStateAction<any>>;
  
  // Actions
  loadSettings: () => Promise<void>;
  saveSettings: (e: React.FormEvent, user: User | null, setUser: React.Dispatch<React.SetStateAction<User | null>>, closeSettingsModal: () => void, checkAuthStatus: () => Promise<void>, getNetworks: () => Promise<number[]>) => Promise<void>;
  loadSubscriptionInfo: () => Promise<void>;
  loadPriceRange: () => Promise<void>;
  loadMaxRestaurants: () => Promise<void>;
  updatePriceRange: (newRange: string) => Promise<void>;
  updateMaxRestaurants: (newMax: number) => Promise<void>;
}

export const useSettings = (isAuthenticated: boolean): UseSettingsReturn => {
  const [settings, setSettings] = useState<AppSettings>({
    restaurant_radius_km: 1.0,
    supercharger_radius_km: 100.0,
    tesla_supercharger: true,
    ionity: false,
    okq8: false,
    unit_system: 'metric'
  });
  
  const [settingsError, setSettingsError] = useState('');
  const [priceRange, setPriceRange] = useState('1,2,3');
  const [maxRestaurants, setMaxRestaurants] = useState(20);
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);

  const loadSettings = async () => {
    const userSettings = await settingsService.loadSettings(isAuthenticated);
    if (userSettings) {
      setSettings(userSettings);
    }
  };

  const loadSubscriptionInfo = async () => {
    const info = await settingsService.loadSubscriptionInfo(isAuthenticated);
    if (info) {
      setSubscriptionInfo(info);
    }
  };

  const loadPriceRange = async () => {
    const priceRange = await settingsService.loadPriceRange(isAuthenticated);
    if (priceRange) {
      setPriceRange(priceRange);
    }
  };

  const loadMaxRestaurants = async () => {
    const maxRestaurants = await settingsService.loadMaxRestaurants(isAuthenticated);
    if (maxRestaurants !== null) {
      setMaxRestaurants(maxRestaurants);
    }
  };

  const updatePriceRange = async (newRange: string) => {
    const success = await settingsService.updatePriceRange(newRange, isAuthenticated);
    if (success) {
      setPriceRange(newRange);
    }
  };

  const updateMaxRestaurants = async (newMax: number) => {
    const success = await settingsService.updateMaxRestaurants(newMax, isAuthenticated);
    if (success) {
      setMaxRestaurants(newMax);
    }
  };

  const saveSettings = async (
    e: React.FormEvent, 
    user: User | null, 
    setUser: React.Dispatch<React.SetStateAction<User | null>>,
    closeSettingsModal: () => void,
    checkAuthStatus: () => Promise<void>,
    getNetworks: () => Promise<number[]>
  ) => {
    e.preventDefault();
    setSettingsError('');
    
    const result = await settingsService.saveSettings(settings);
    
    if (result.success) {
      // Save unit system if changed
      if (settings.unit_system) {
        try {
          await settingsService.saveUnitSystem(settings.unit_system, true);
        } catch (error) {
          console.error('Error saving unit system:', error);
        }
      }

      // Save display name if changed
      if (user?.displayName && user.displayName.trim()) {
        try {
          const displayNameResponse = await fetch(buildApiUrl('/api/settings/display-name'), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ displayName: user.displayName.trim() })
          });

          if (displayNameResponse.ok) {
            // Refresh user data to show updated display name
            console.log('🔄 Calling checkAuthStatus after display name update...');
            await checkAuthStatus();
          }
        } catch (error) {
          console.error('Error saving display name:', error);
        }
      }
      
      closeSettingsModal();
      // Refresh networks after saving settings
      await getNetworks();
    } else {
      setSettingsError(result.error || 'Failed to save settings');
    }
  };

  return {
    // State
    settings,
    setSettings,
    settingsError,
    setSettingsError,
    priceRange,
    setPriceRange,
    maxRestaurants,
    setMaxRestaurants,
    subscriptionInfo,
    setSubscriptionInfo,
    
    // Actions
    loadSettings,
    saveSettings,
    loadSubscriptionInfo,
    loadPriceRange,
    loadMaxRestaurants,
    updatePriceRange,
    updateMaxRestaurants,
  };
};